import type { QuestionDraft } from './survey-questions-form'
import { SurveyQuestionKind } from '@zeus/index'
import { ArrowDown, ArrowUp, Trash2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '~/components/ui/button'
import { Input } from '~/components/ui/input'
import { SURVEY_LIMITS } from './survey-questions-form'

type IProps = {
  draft: QuestionDraft
  index: number
  count: number
  overallName: string
  onChange: (draft: QuestionDraft) => void
  onOverall: () => void
  onMove: (by: -1 | 1) => void
  onRemove: () => void
}

/** Une question du questionnaire (#320) : son libellé, sa nature, son rang. */
function SurveyQuestionRow({ draft, index, count, overallName, onChange, onOverall, onMove, onRemove }: IProps) {
  const { t } = useTranslation()
  const name = t('surveyEditor.question', { number: index + 1 })

  return (
    <li className="flex flex-col gap-3 rounded-lg border border-border p-4">
      <div className="flex flex-wrap items-end gap-3">
        <div className="flex min-w-60 flex-1 flex-col gap-1.5">
          <label className="text-sm font-medium" htmlFor={`q-label-${draft.key}`}>{name}</label>
          <Input id={`q-label-${draft.key}`} maxLength={SURVEY_LIMITS.label} value={draft.label} onChange={e => onChange({ ...draft, label: e.target.value })} />
        </div>
        <div className="flex flex-col gap-1.5">
          <label className="text-sm font-medium" htmlFor={`q-kind-${draft.key}`}>{t('surveyEditor.kind')}</label>
          <select
            id={`q-kind-${draft.key}`}
            value={draft.kind}
            onChange={e => onChange({ ...draft, kind: e.target.value as SurveyQuestionKind, isOverall: draft.isOverall && e.target.value === SurveyQuestionKind.SCALE })}
            className="h-10 rounded-md border border-input bg-transparent px-3 text-sm"
          >
            <option value={SurveyQuestionKind.SCALE}>{t('surveyEditor.kind.scale')}</option>
            <option value={SurveyQuestionKind.TEXT}>{t('surveyEditor.kind.text')}</option>
          </select>
        </div>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <label className="flex items-center gap-2 text-sm">
          <input type="radio" name={overallName} checked={draft.isOverall} disabled={draft.kind !== SurveyQuestionKind.SCALE} onChange={onOverall} />
          {t('surveyEditor.overall')}
        </label>
        <div className="flex gap-1">
          <Button variant="ghost" size="icon" disabled={index === 0} onClick={() => onMove(-1)} aria-label={t('surveyEditor.moveUp', { name })}><ArrowUp className="size-4" /></Button>
          <Button variant="ghost" size="icon" disabled={index === count - 1} onClick={() => onMove(1)} aria-label={t('surveyEditor.moveDown', { name })}><ArrowDown className="size-4" /></Button>
          <Button variant="ghost" size="icon" onClick={onRemove} aria-label={t('surveyEditor.remove', { name })}><Trash2 className="size-4" /></Button>
        </div>
      </div>
    </li>
  )
}

export default SurveyQuestionRow
